import React, { ReactNode, useMemo } from 'react';
import { Checkbox } from '../controls/Checkbox';
import styles from './DataGrid.scss';

export type DataGridColumnWidth = '10' | '20' | '25' | '33' | '50' | '75';

export enum DisplayMode {
  Dynamic,
  Fixed,
}

export interface DataGridColumn<T> {
  /**
   * Column name
   */
  label: string | JSX.Element;
  /**
   * Callback to render the column value
   */
  value: (r: T) => ReactNode;
  /**
   * Use right alignment
   */
  highlight?: boolean | ((r: T) => boolean);
  /**
   * Use right alignment
   */
  rightAlign?: boolean | ((r: T) => boolean);
  /**
   * Column width
   */
  width?: DataGridColumnWidth;
  /**
   * Is column sortable
   */
  sortable?: boolean;
  /**
   * Footer
   */
  footer?: string | number | JSX.Element | string[] | number[] | JSX.Element[];
}

export interface DataGridSortOptions {
  columnIndex: number;
  descending: boolean;
  onChange: (columnIndex: number, descending: boolean) => void;
}

export interface DataGridSelectOptions<T> {
  rows: T[];
  onChange: (selectedRows: T[]) => void;
}

export interface DataGridExpandOptions<T> {
  rows: (T | null)[];
  content: (row: T | null) => ReactNode | ReactNode[];
  canExpand?: (row: T) => boolean;
  onChange: (selectedRows: (T | null)[]) => void;
  onGetRowId?: (row: T | null) => string;
}

export interface DataGridProps<T, C extends DataGridColumn<T> = DataGridColumn<T>> {
  /**
   * Columns to be displayed by the Data Grid
   */
  columns: C[];

  /**
   * Rows to be dislayed by the Data Grid
   */
  rows: T[];

  /**
   * Message displayed when there are no rows
   */
  noRowsMessage?: string | JSX.Element;

  /**
   * Sorting options
   */
  sort?: DataGridSortOptions;
  /**
   * Selection options
   */
  select?: DataGridSelectOptions<T>;
  /**
   * Expand options
   */
  expand?: DataGridExpandOptions<T>;

  /**
   * Cell display options
   */
  displayMode?: DisplayMode;
}

export function mergeRows<T>(rows: T[], existing: T | null, updated: T | null): T[] {
  const newRows = rows.slice();
  var index = existing != null ? newRows.indexOf(existing) : -1;
  if (index >= 0) {
    if (updated) {
      newRows.splice(index, 1, updated);
    } else {
      newRows.splice(index, 1);
    }
  } else if (updated) {
    newRows.push(updated);
  }

  return newRows;
}

export function sortRows<T>(rows: T[], columns: DataGridColumn<T>[], columnIndex: number, descending: boolean): T[] {
  if (columnIndex < 0 || columnIndex >= columns.length) {
    return rows;
  }
  const column = columns[columnIndex];
  const newRows = rows.slice();
  newRows.sort((a, b) => {
    const aVal = column.value(a) as any;
    const bVal = column.value(b) as any;
    if (aVal > bVal) {
      return 1 * (descending ? 1 : -1);
    } else if (aVal < bVal) {
      return -1 * (descending ? 1 : -1);
    } else {
      return 0;
    }
  });
  return newRows;
}

/**
 * Renders Data Grid using configured columns and rows
 */
export const DataGrid = <T extends {}, C extends DataGridColumn<T> = DataGridColumn<T>>(props: DataGridProps<T, C>) => {
  const colSpan = useMemo(() => {
    return props.columns.length + (props.expand ? 1 : 0) + (props.select ? 1 : 0);
  }, [props.columns, props.expand ? true : false]);

  const toggleExpanded = (row: T | null) => {
    let res: (T | null)[];
    const options = props.expand;
    if (!options) {
      return;
    }
    const rows = options.rows;
    if (rows.indexOf(row) >= 0) {
      res = rows.filter((r) => r != row);
    } else {
      res = [row];
    }
    options.onChange && options.onChange(res);
  };

  const renderDetails = (r: T | null) => {
    const options = props.expand;
    if (!options) {
      return;
    }
    return (
      <tr className={styles['au-table__row']}>
        <td
          colSpan={colSpan}
          className={['acma-details-cell', 'au-table__cell']
            .map((r) => styles[r])
            .concat('px-0')
            .join(' ')}>
          <div className="container-fluid">{options.content && options.content(r)}</div>
        </td>
      </tr>
    );
  };

  const renderSorting = (column: C, columnIndex: number) => {
    const { sortable = true } = column;
    if (!props.sort || !sortable) {
      return <></>;
    }
    if (props.sort.columnIndex == columnIndex) {
      return <>&nbsp;{props.sort.descending ? <span>{'\u2191'}</span> : <span>{'\u2193'}</span>}</>;
    } else {
      return (
        <>
          &nbsp;<span>{'\u2191'}</span>
          <span>{'\u2193'}</span>
        </>
      );
    }
  };

  const onSort = (column: C, columnIndex: number) => {
    const { sortable = true } = column;
    if (!props.sort || !sortable) {
      return;
    }
    if (props.sort.columnIndex == columnIndex) {
      if (props.sort.descending) {
        props.sort.onChange(columnIndex, !props.sort.descending);
      } else {
        props.sort.onChange(-1, !props.sort.descending);
      }
    } else {
      props.sort.onChange(columnIndex, true);
    }
  };

  const allSelected = useMemo(() => {
    if (props.rows.length == 0) {
      return false;
    }
    return props.select ? props.select.rows.filter((r) => props.rows.indexOf(r) >= 0).length == props.rows.length : false;
  }, [props.select, props.select ? props.select.rows : null, props.rows]);

  const { columns, rows } = props;
  return (
    <div className={styles['au-table__wrapper']}>
      <table className={styles['au-table']}>
        <thead className={styles['au-table__head']}>
          {props.expand && props.expand.rows.indexOf(null) >= 0 && renderDetails(null)}
          <tr className={styles['au-table__row']}>
            {props.select && (
              <th className={styles['au-table__header']} style={{ width: '0px' }}>
                <Checkbox
                  id="all"
                  label={<>&nbsp;</>}
                  checked={allSelected}
                  onChange={(v) => props.select && props.select.onChange(v ? props.rows.slice() : [])}
                />
              </th>
            )}
            {columns.map((c, i) => {
              const temp = ['au-table__header'];
              if (c.rightAlign) {
                temp.push('au-table__header--numeric');
              }
              if (c.width) {
                temp.push(`au-table__header--width-${c.width}`);
              }
              return (
                <th
                  key={i}
                  scope="col"
                  className={temp.map((c) => styles[c]).join(' ')}
                  style={props.sort && (c.sortable || c.sortable == undefined) ? { cursor: 'pointer' } : {}}
                  onClick={() => onSort(c, i)}>
                  {c.label}
                  {renderSorting(c, i)}
                </th>
              );
            })}
            {props.expand ? <th className={styles['au-table__header']} style={{ width: '0px' }}></th> : <></>}
          </tr>
        </thead>
        <tbody className={styles['au-table__body']}>
          {rows.map((r, i) => {
            const expanded = props.expand && props.expand.rows.indexOf(r) >= 0;
            const canExpand = props.expand ? (props.expand.canExpand ? props.expand.canExpand(r) : true) : false;
            const rowId = props.expand && props.expand.onGetRowId ? props.expand.onGetRowId(r) : undefined;
            return (
              <React.Fragment key={i}>
                <tr className={styles['au-table__row']} id={rowId}>
                  {props.select && (
                    <td className={styles['au-table__cell']} style={{ width: '0px' }}>
                      <Checkbox
                        id={`${i}`}
                        label={<>&nbsp;</>}
                        checked={props.select.rows.indexOf(r) >= 0}
                        onChange={(v) => {
                          if (!props.select) {
                            return;
                          }
                          const newRows = props.select.rows.filter((s) => s != r);
                          if (v) {
                            newRows.push(r);
                          }
                          props.select.onChange(newRows);
                        }}
                      />
                    </td>
                  )}
                  {columns.map((c, j) => {
                    const temp = ['au-table__cell'];
                    if (c.rightAlign) {
                      temp.push('au-table__cell--numeric');
                    }

                    if (c.highlight && (typeof c.highlight == 'boolean' || c.highlight(r))) {
                      temp.push('acma-higlight-cell');
                    }
                    if (props.displayMode == DisplayMode.Fixed) {
                      temp.push(`au-table__cell--nowrap`);
                    }
                    return (
                      <td key={j} className={temp.map((c) => styles[c]).join(' ')}>
                        {c.value(r)}
                      </td>
                    );
                  })}
                  {canExpand ? (
                    <td className={['acma-expand-cell', 'au-table__cell'].map((r) => styles[r]).join(' ')} style={{ width: '0px' }} id={rowId ? rowId + '-expand' : undefined}>
                      <a
                        href="#"
                        className={styles['acma-expand-cell__link']}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            toggleExpanded(r);
                          }
                        }}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleExpanded(r);
                        }}>
                        {expanded ? '\u02C4' : '\u02C5'}
                        {expanded ? <span className="sr-only">Collapse</span> : <span className="sr-only">Expand</span>}
                      </a>
                    </td>
                  ) : (
                    <td></td>
                  )}
                </tr>
                {expanded && renderDetails(r)}
              </React.Fragment>
            );
          })}
          {rows.length > 0 && columns.findIndex((i) => i.footer) !== -1 && !Array.isArray(columns[0].footer) && (
            <tr className={styles['au-table__row']}>
              {columns.map((c, j) => {
                const temp = ['au-table__cell'];
                if (c.rightAlign) {
                  temp.push('au-table__cell--numeric');
                }

                if (props.displayMode == DisplayMode.Fixed) {
                  temp.push(`au-table__cell--nowrap`);
                }
                return (
                  <td key={j} className={temp.map((c) => styles[c]).join(' ')}>
                    <b>{c.footer}</b>
                  </td>
                );
              })}
            </tr>
          )}
          {rows.length > 0 && columns.findIndex((i) => i.footer) !== -1 && Array.isArray(columns[0].footer) && (
            <>
              {columns[0].footer.map((r, i) => {
                return (
                  <tr key={r.toString()} className={styles['au-table__row']}>
                    {columns.map((c, j) => {
                      let footers: any[] = c.footer as any;
                      const temp = ['au-table__cell'];
                      if (c.rightAlign) {
                        temp.push('au-table__cell--numeric');
                      }

                      if (props.displayMode == DisplayMode.Fixed) {
                        temp.push(`au-table__cell--nowrap`);
                      }
                      if (c.footer && c.footer !== '') {
                        //Cater for Form Grid which adds empty row for footer.
                        return (
                          <td key={j} className={temp.map((c) => styles[c]).join(' ')}>
                            <b>{footers[i]}</b>
                          </td>
                        );
                      } else {
                        return (
                          <React.Fragment key={j}>
                            <td colSpan={props.expand ? 2 : 1}></td>
                          </React.Fragment>
                        );
                      }
                    })}
                  </tr>
                );
              })}
            </>
          )}
          {rows.length == 0 && props.noRowsMessage ? (
            <tr className={styles['au-table__row']}>
              <td
                colSpan={colSpan}
                className={['au-table__cell']
                  .map((r) => styles[r])
                  .concat('px-0')
                  .join(' ')}>
                <div className="container-fluid">{props.noRowsMessage}</div>
              </td>
            </tr>
          ) : (
            <></>
          )}
        </tbody>
      </table>
    </div>
  );
};
